import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { skillsAPI } from '@/services/api';
import type { CreateSkillDto, Skill, UpdateSkillDto } from '@/types/domain';

export interface UseSkillsReturn {
  skills: Skill[];
  loading: boolean;
  error: string;
  success: string;
  refresh: () => Promise<void>;
  create: (data: CreateSkillDto) => Promise<void>;
  update: (id: string, data: UpdateSkillDto) => Promise<void>;
  delete: (id: string) => Promise<void>;
  clearMessages: () => void;
  setError: (error: string) => void;
}

/**
 * 技能数据管理 Hook
 * 封装技能的增删改查操作和状态管理
 */
export function useSkills(): UseSkillsReturn {
  const { t } = useTranslation();
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // 组件挂载时加载技能列表
  useEffect(() => {
    void loadSkills();
  }, []);

  /**
   * 加载技能列表
   */
  const loadSkills = useCallback(async () => {
    try {
      setLoading(true);
      const response = await skillsAPI.getAll();
      setSkills(response.skills || []);
    } catch (requestError: any) {
      console.error('Failed to load skills:', requestError);
      if (requestError.response?.status === 401) {
        setError(t('skills.sessionExpired'));
      } else {
        setError(requestError.response?.data?.error || t('skills.loadFailed'));
      }
      setSkills([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  /**
   * 创建新技能
   */
  const create = useCallback(
    async (data: CreateSkillDto) => {
      try {
        await skillsAPI.create(data);
        setSuccess(t('skills.createdSuccess'));
        await loadSkills();
      } catch (requestError: any) {
        const errorMessage =
          requestError.response?.data?.error || requestError.message || t('skills.createFailed');
        setError(errorMessage);
        throw requestError;
      }
    },
    [t, loadSkills]
  );

  /**
   * 更新技能
   */
  const update = useCallback(
    async (id: string, data: UpdateSkillDto) => {
      try {
        await skillsAPI.update(id, data);
        setSuccess(t('skills.updatedSuccess'));
        await loadSkills();
      } catch (requestError: any) {
        const errorMessage =
          requestError.response?.data?.error || requestError.message || t('skills.updateFailed');
        setError(errorMessage);
        throw requestError;
      }
    },
    [t, loadSkills]
  );

  /**
   * 删除技能
   */
  const deleteSkill = useCallback(
    async (id: string) => {
      try {
        await skillsAPI.delete(id);
        setSuccess(t('skills.deletedSuccess'));
        await loadSkills();
      } catch (requestError: any) {
        setError(requestError.response?.data?.error || t('skills.deleteFailed'));
        throw requestError;
      }
    },
    [t, loadSkills]
  );

  /**
   * 清除错误和成功消息
   */
  const clearMessages = useCallback(() => {
    setError('');
    setSuccess('');
  }, []);

  return {
    skills,
    loading,
    error,
    success,
    refresh: loadSkills,
    create,
    update,
    delete: deleteSkill,
    clearMessages,
    setError,
  };
}
